import React from "react";
import { Modal } from "antd";
import { useMediaQuery } from "react-responsive";

// Mirror the page-wrapper's sidebar offset so the modal visually centers
// within the content area (right of the sidebar), not the raw viewport.
const DELETE_WRAP_STYLES = `
  @media (min-width: 992px) {
    .delete-modal-wrap { padding-left: 250px; }
    body.mini-sidebar .delete-modal-wrap { padding-left: 60px; }
  }
`;

/**
 * Confirmation modal for destructive actions.
 *
 * Props:
 *   deleteModal:    boolean, controls open state
 *   setDeleteModal: setter, used to close
 *   deleteHandler:  function called when Delete is confirmed
 *   Name:           optional title override (e.g. "Delete Warehouse").
 *                   Defaults to "Delete Record".
 *   message:        optional custom body message
 *   itemName:       optional name of the specific item being deleted; shown
 *                   in quotes for extra confirmation
 *   loading:        optional, disables buttons + shows spinner on Delete
 */
const DeleteModal = ({
  deleteModal,
  setDeleteModal,
  deleteHandler,
  Name = "Delete Record",
  message,
  itemName,
  loading = false,
}) => {
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  const handleClose = () => {
    if (loading) return;
    setDeleteModal(false);
  };

  const handleConfirm = () => {
    if (loading) return;
    if (typeof deleteHandler === "function") deleteHandler();
  };

  const defaultMessage = itemName
    ? `Are you sure you want to delete "${itemName}"? This action cannot be undone.`
    : "Are you sure you want to delete this? This action cannot be undone.";

  return (
    <>
      <style>{DELETE_WRAP_STYLES}</style>
      <Modal
        open={deleteModal}
        onCancel={handleClose}
        footer={null}
        width={isMobile ? "100%" : 440}
        centered
        mask={false}
        maskClosable={false}
        destroyOnClose
        closable={false}
        wrapClassName="delete-modal-wrap"
        bodyStyle={{ padding: 0 }}
      >
        <div style={{ padding: "28px 24px 22px", textAlign: "center" }}>
          {/* Danger icon */}
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              background: "#fee2e2",
              color: "#dc2626",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 14,
            }}
          >
            <i
              className="la la-trash-alt"
              style={{ fontSize: 26 }}
              aria-hidden="true"
            />
          </div>

          {/* Title */}
          <div
            style={{
              fontSize: 18,
              fontWeight: 700,
              color: "#111827",
              marginBottom: 6,
              letterSpacing: "-0.01em",
            }}
          >
            {Name}
          </div>

          {/* Description */}
          <div
            style={{
              fontSize: 13.5,
              color: "#4b5563",
              lineHeight: 1.5,
              marginBottom: 22,
              padding: "0 6px",
            }}
          >
            {message || defaultMessage}
          </div>

          {/* Actions */}
          <div
            style={{
              display: "flex",
              gap: 10,
              justifyContent: "center",
            }}
          >
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="btn"
              style={{
                minWidth: 120,
                padding: "10px 20px",
                borderRadius: 8,
                fontWeight: 600,
                fontSize: 14,
                background: "#ffffff",
                color: "#374151",
                border: "1px solid #d1d5db",
                cursor: loading ? "not-allowed" : "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={loading}
              className="btn"
              style={{
                minWidth: 120,
                padding: "10px 20px",
                borderRadius: 8,
                fontWeight: 600,
                fontSize: 14,
                background: loading ? "#f3aab3" : "#dc2626",
                color: "#ffffff",
                border: "none",
                cursor: loading ? "not-allowed" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
              }}
            >
              {loading && (
                <i
                  className="fa fa-spinner fa-spin"
                  style={{ fontSize: 13 }}
                  aria-hidden="true"
                />
              )}
              Delete
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default DeleteModal;
